/*
 * <CMSConsole/> — full CMS admin console (Blocks / Intros / Walkthroughs /
 * Media Library / Activity).
 *
 * Blocks tab: page picker → grouped list of blocks (by section) → per-block
 * editor with inline History drawer for version rollback.
 * Intros + Walkthroughs tabs: embed the legacy consoles inline so admins
 * only see one "Content CMS" top-level admin tab (previous separate
 * Intros/Walkthroughs top-level tabs were retired).
 * Media Library: standalone browser view of the uploads + external URLs.
 * Activity: recent CMS edits, top-edited blocks, editor leaderboard.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Save, RotateCcw, ImageIcon, Video, FileText, ExternalLink, Search, ListChecks, Plus, Trash2, X, ArrowUpRight, Upload, History, LayoutGrid, Compass, MapPin, Activity, User, Clock } from "lucide-react";
import MediaPicker from "./MediaPicker";
import { IntrosConsole, WalkthroughsConsole } from "../AdminContentConsoles";

const PAGE_META = {
  landing:  { label: "Landing (marketing home)", href: "/" },
  pricing:  { label: "Pricing page",             href: "/account/subscription" },
  footer:   { label: "Footer (site-wide)",       href: "/" },
};

const TYPE_ICONS = {
  text:     FileText, richtext: FileText,
  image:    ImageIcon, video:   Video,
  url:      ExternalLink,
  list:     ListChecks,
};

const SUBTABS = [
  { id: "blocks",       label: "Content Blocks", icon: LayoutGrid },
  { id: "intros",       label: "Intros",         icon: Compass },
  { id: "walkthroughs", label: "Walkthroughs",   icon: MapPin },
  { id: "media",        label: "Media Library",  icon: ImageIcon },
  { id: "activity",     label: "Activity",       icon: Activity },
];


export default function CMSConsole() {
  const [subtab, setSubtab] = useState("blocks");
  return (
    <div data-testid="admin-cms-console">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Content CMS
          </p>
          <h2 className="font-display text-2xl mt-1">Everything editable on the public site</h2>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            Content blocks, intros, walkthrough tours, media library, and edit history — all in one place.
          </p>
        </div>
      </div>

      <div className="inline-flex rounded-full border border-shell-border bg-white p-1 mb-6 flex-wrap" data-testid="cms-subtabs">
        {SUBTABS.map((t) => {
          const Icon = t.icon;
          const active = subtab === t.id;
          return (
            <button
              key={t.id} type="button"
              onClick={() => setSubtab(t.id)}
              data-testid={`cms-subtab-${t.id}`}
              className={[
                "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition",
                active ? "bg-primary text-white shadow" : "text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              <Icon className="w-3 h-3" /> {t.label}
            </button>
          );
        })}
      </div>

      {subtab === "blocks"       && <BlocksTab />}
      {subtab === "intros"       && <IntrosConsole />}
      {subtab === "walkthroughs" && <WalkthroughsConsole />}
      {subtab === "media"        && <MediaLibraryPanel />}
      {subtab === "activity"     && <ActivityPanel />}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
 * Blocks tab (formerly the top-level content) — page picker + block list.
 * ═══════════════════════════════════════════════════════════════════════ */
function BlocksTab() {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState("landing");
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPages = async () => {
    try {
      const { data } = await api.get("/admin/cms/pages");
      setPages(data.pages || []);
    } catch (err) { toast.error(formatError(err)); }
  };

  // `silent=true` refetch keeps the existing UI mounted so save→reset
  // doesn't flash a spinner (which would tear down BlockRow state).
  const loadBlocks = async (page, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/admin/cms/pages/${page}`);
      setBlocks(data.blocks || []);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadPages(); }, []);
  useEffect(() => { loadBlocks(selectedPage); }, [selectedPage]);

  const sections = useMemo(() => {
    const grouped = {};
    for (const b of blocks) {
      const s = b.section || "misc";
      if (!grouped[s]) grouped[s] = [];
      grouped[s].push(b);
    }
    return grouped;
  }, [blocks]);

  const meta = PAGE_META[selectedPage] || { label: selectedPage, href: "/" };
  const pageRow = pages.find((p) => p.page === selectedPage);

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Content Blocks
          </p>
          <p className="text-sm text-muted-foreground max-w-2xl mt-1">
            Edit copy, images, videos and repeatable lists across every public marketing page. Hover any element on the live page for the pencil.
          </p>
        </div>
        <a href={meta.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-4 py-2 text-[11px] font-semibold hover:border-primary/40 transition" data-testid="cms-open-live">
          Open live <ArrowUpRight className="w-3 h-3" />
        </a>
      </div>

      {/* Page picker */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6" data-testid="cms-page-picker">
        {pages.map((p) => (
          <button
            key={p.page} type="button"
            onClick={() => setSelectedPage(p.page)}
            data-testid={`cms-pick-page-${p.page}`}
            className={[
              "text-left rounded-2xl border p-4 transition",
              selectedPage === p.page ? "border-primary bg-primary/5" : "border-shell-border bg-white hover:border-primary/40",
            ].join(" ")}
          >
            <p className="font-semibold text-sm">{PAGE_META[p.page]?.label || p.page}</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              {p.block_count} blocks · {p.override_count} custom
            </p>
          </button>
        ))}
      </div>

      {/* Blocks by section */}
      {loading ? (
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-5">
          {pageRow && (
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1 rounded-full bg-shell-border/40 px-2.5 py-1">
                {pageRow.block_count} blocks
              </span>
              {pageRow.override_count > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-800 px-2.5 py-1 font-semibold">
                  {pageRow.override_count} customised
                </span>
              )}
            </div>
          )}
          {Object.entries(sections).map(([section, secBlocks]) => (
            <SectionCard key={section} section={section} blocks={secBlocks} onSaved={() => { loadBlocks(selectedPage, { silent: true }); loadPages(); }} />
          ))}
        </div>
      )}
    </div>
  );
}


/* ── Section card — groups blocks belonging to one page section ─────── */
function SectionCard({ section, blocks, onSaved }) {
  return (
    <div className="rounded-2xl border border-shell-border bg-white p-5" data-testid={`cms-section-${section}`}>
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">{section}</p>
      <div className="mt-3 divide-y divide-shell-border">
        {blocks.map((b) => (
          <BlockRow key={b.id} block={b} onSaved={onSaved} />
        ))}
      </div>
    </div>
  );
}


/* ── One editable block row — type-aware inline editor ─────────────── */
function BlockRow({ block: initialBlock, onSaved }) {
  // Hold a local copy of the block so save/reset can optimistically flip
  // `is_override` and `value` without waiting for the parent's silent
  // refetch to land. Kept in sync with the parent via useEffect.
  const [block, setBlock] = useState(initialBlock);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(block.value);
  const [saving, setSaving] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => { setBlock(initialBlock); }, [initialBlock]);
  useEffect(() => { setDraft(block.value); }, [block.value]);

  const Icon = TYPE_ICONS[block.type] || FileText;

  const save = async (newValue = draft) => {
    setSaving(true);
    try {
      await api.put(`/admin/cms/blocks/${block.id}`, { value: newValue });
      // Optimistic flip so the Custom badge + Reset button render
      // immediately, without a spinner remount.
      setBlock((b) => ({ ...b, value: newValue, is_override: true }));
      toast.success("Saved");
      setEditing(false);
      onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  const doReset = async () => {
    if (!window.confirm("Reset to default and delete the current override?")) return;
    try {
      await api.post(`/admin/cms/blocks/${block.id}/reset`);
      setBlock((b) => ({ ...b, value: b.default, is_override: false }));
      toast.success("Reset to default");
      onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };

  const summarize = (v) => {
    if (v == null) return <em className="text-muted-foreground">Empty</em>;
    if (typeof v === "string") return v.length > 140 ? v.slice(0, 140) + "…" : v;
    if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? "" : "s"}`;
    return JSON.stringify(v);
  };

  return (
    <div className="py-4" data-testid={`cms-block-${block.id}`}>
      <div className="flex items-start gap-3">
        <span className="mt-1 w-8 h-8 rounded-lg bg-shell-border/40 grid place-items-center shrink-0">
          <Icon className="w-4 h-4 text-primary" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-sm">{block.label}</p>
            <code className="text-[10px] font-mono text-muted-foreground">{block.id}</code>
            {block.is_override && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-800 border border-emerald-500/30 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em]">
                Custom
              </span>
            )}
            <span className="ml-auto text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">{block.type}</span>
            <button type="button" onClick={() => setHistoryOpen(true)}
                    className="cms-btn cms-btn-ghost" title="Version history"
                    data-testid={`cms-block-history-${block.id}`}>
              <History className="w-3 h-3" /> History
            </button>
          </div>

          {block.updated_by_email && (
            <p className="text-[10px] text-muted-foreground mt-0.5 inline-flex items-center gap-1.5">
              <User className="w-2.5 h-2.5" />
              Last edited by <span className="font-semibold">{block.updated_by_email}</span>
              {block.updated_at && (
                <> · <RelativeTime iso={block.updated_at} /></>
              )}
            </p>
          )}
          {/* Preview / editor */}
          {block.type === "image" && (
            <div className="mt-2 flex items-start gap-3">
              <img src={block.value ? (block.value.startsWith("/api/") ? `${BACKEND_URL}${block.value}` : block.value) : ""}
                   alt={block.label} className="w-32 h-20 object-cover rounded-lg border border-shell-border bg-shell-border/20" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground break-all">{block.value}</p>
                <div className="mt-2 flex items-center gap-2">
                  <button type="button" onClick={() => setMediaOpen(true)} className="cms-btn cms-btn-primary" data-testid={`cms-block-pick-${block.id}`}>
                    <Upload className="w-3 h-3" /> Change image
                  </button>
                  {block.is_override && (
                    <button type="button" onClick={doReset} className="cms-btn" data-testid={`cms-block-reset-${block.id}`}>
                      <RotateCcw className="w-3 h-3" /> Reset
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {block.type === "video" && (
            <div className="mt-2 flex items-start gap-3">
              <div className="w-32 h-20 rounded-lg border border-shell-border bg-shell-side text-white grid place-items-center shrink-0">
                <Video className="w-6 h-6 opacity-70" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground break-all">{block.value || <em>Empty</em>}</p>
                <div className="mt-2 flex items-center gap-2">
                  <button type="button" onClick={() => setMediaOpen(true)} className="cms-btn cms-btn-primary" data-testid={`cms-block-pick-${block.id}`}>
                    <Upload className="w-3 h-3" /> Change video
                  </button>
                  {block.is_override && (
                    <button type="button" onClick={doReset} className="cms-btn" data-testid={`cms-block-reset-${block.id}`}>
                      <RotateCcw className="w-3 h-3" /> Reset
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {(block.type === "text" || block.type === "richtext" || block.type === "url") && (
            <>
              {!editing ? (
                <div className="mt-1">
                  <p className="text-sm text-foreground whitespace-pre-wrap break-words">{summarize(block.value)}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <button type="button" onClick={() => setEditing(true)} className="cms-btn cms-btn-primary" data-testid={`cms-block-edit-${block.id}`}>
                      Edit
                    </button>
                    {block.is_override && (
                      <button type="button" onClick={doReset} className="cms-btn" data-testid={`cms-block-reset-${block.id}`}>
                        <RotateCcw className="w-3 h-3" /> Reset
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-1">
                  {block.type === "richtext" ? (
                    <textarea rows={4} value={draft || ""} onChange={(e) => setDraft(e.target.value)}
                      className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
                      data-testid={`cms-block-input-${block.id}`}
                    />
                  ) : (
                    <input type={block.type === "url" ? "url" : "text"} value={draft || ""} onChange={(e) => setDraft(e.target.value)}
                      className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40"
                      data-testid={`cms-block-input-${block.id}`}
                    />
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <button type="button" onClick={() => save()} disabled={saving} className="cms-btn cms-btn-primary" data-testid={`cms-block-save-${block.id}`}>
                      {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                    </button>
                    <button type="button" onClick={() => { setDraft(block.value); setEditing(false); }} className="cms-btn cms-btn-ghost">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {block.type === "list" && (
            <ListEditor block={block} onSaved={onSaved} />
          )}
        </div>
      </div>

      {mediaOpen && (
        <MediaPicker
          kind={block.type === "video" ? "video" : "image"}
          currentUrl={block.value}
          onClose={() => setMediaOpen(false)}
          onPick={async (item) => { await save(item.url); setMediaOpen(false); }}
        />
      )}
      {historyOpen && (
        <VersionHistoryDrawer
          block={block}
          onClose={() => setHistoryOpen(false)}
          onRestored={(newValue) => {
            setBlock((b) => ({ ...b, value: newValue, is_override: true }));
            onSaved();
          }}
        />
      )}
    </div>
  );
}


/* ── Version history drawer — lists every save/reset/restore and lets the
 * admin click "Restore" on any past value to bring it back. ───────────── */
function VersionHistoryDrawer({ block, onClose, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cms/blocks/${block.id}/versions`);
      setVersions(data.versions || []);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [block.id]);

  const restore = async (vid, value) => {
    if (!window.confirm("Restore this version? A new history entry will be created.")) return;
    setRestoring(vid);
    try {
      const { data } = await api.post(`/admin/cms/blocks/${block.id}/versions/${vid}/restore`);
      toast.success("Restored");
      onRestored(data.value);
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setRestoring(null); }
  };

  const summarizeValue = (v) => {
    if (v == null) return "(empty)";
    if (typeof v === "string") return v.length > 100 ? v.slice(0, 100) + "…" : v;
    if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? "" : "s"}`;
    return JSON.stringify(v).slice(0, 100);
  };

  return (
    <div className="cms-drawer-backdrop" onClick={onClose}>
      <div className="cms-drawer" onClick={(e) => e.stopPropagation()} data-testid={`cms-history-drawer-${block.id}`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-shell-border">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary flex items-center gap-1.5">
              <History className="w-3 h-3" /> Version History
            </p>
            <h3 className="font-display text-xl mt-0.5">{block.label}</h3>
            <code className="text-[10px] font-mono text-muted-foreground">{block.id}</code>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-shell-border/30" data-testid={`cms-history-close-${block.id}`}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 flex-1 overflow-auto">
          {loading ? (
            <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
            </div>
          ) : versions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic" data-testid={`cms-history-empty-${block.id}`}>
              No edits yet. Save the block once to start the history.
            </p>
          ) : (
            <ol className="relative border-l-2 border-shell-border/60 ml-2 space-y-3">
              {versions.map((v, idx) => (
                <li key={v.id} className="ml-5 relative" data-testid={`cms-history-entry-${idx}`}>
                  <span className={[
                    "absolute -left-[26px] top-1 w-3 h-3 rounded-full border-2 border-white",
                    v.action === "reset" ? "bg-slate-400" :
                    v.action === "restore" ? "bg-sky-500" : "bg-primary",
                  ].join(" ")} />
                  <div className="rounded-xl border border-shell-border bg-white p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={[
                        "inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em]",
                        v.action === "reset"   ? "bg-slate-500/10 text-slate-700" :
                        v.action === "restore" ? "bg-sky-500/10 text-sky-800" :
                                                  "bg-primary/10 text-primary",
                      ].join(" ")}>
                        {v.action}
                      </span>
                      <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" /> <RelativeTime iso={v.created_at} />
                      </span>
                      <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                        <User className="w-2.5 h-2.5" /> {v.created_by_email || "unknown"}
                      </span>
                      {idx === 0 && (
                        <span className="ml-auto inline-flex items-center rounded-full bg-emerald-500/15 text-emerald-800 border border-emerald-500/30 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em]">
                          current
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-foreground break-words whitespace-pre-wrap">{summarizeValue(v.value)}</p>
                    {idx > 0 && (
                      <div className="mt-2">
                        <button type="button" onClick={() => restore(v.id, v.value)} disabled={restoring === v.id} className="cms-btn cms-btn-primary" data-testid={`cms-history-restore-${idx}`}>
                          {restoring === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Restore this version
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="px-5 py-3 border-t border-shell-border text-[11px] text-muted-foreground">
          History is capped at the last {30} entries per block.
        </div>
      </div>
    </div>
  );
}


/* ── Small "5m ago" style relative timestamp ─────────────────────────── */
function RelativeTime({ iso }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return <span>{iso}</span>;
  const s = Math.max(0, Math.floor((now - d) / 1000));
  if (s < 60)   return <span title={iso}>{s}s ago</span>;
  if (s < 3600) return <span title={iso}>{Math.floor(s / 60)}m ago</span>;
  if (s < 86400) return <span title={iso}>{Math.floor(s / 3600)}h ago</span>;
  return <span title={iso}>{Math.floor(s / 86400)}d ago</span>;
}


/* ── Inline list editor — for list-typed blocks in the console ──────── */
function ListEditor({ block, onSaved }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(block.value || []);
  const [saving, setSaving] = useState(false);
  const shape = block.list_shape || [];

  useEffect(() => { if (open) setRows(block.value || []); }, [open, block.value]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/cms/blocks/${block.id}`, { value: rows });
      toast.success("List saved");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };
  const doReset = async () => {
    if (!window.confirm("Reset list to default?")) return;
    try {
      await api.post(`/admin/cms/blocks/${block.id}/reset`);
      toast.success("Reset");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };
  const addRow = () => {
    const blank = {};
    shape.forEach((f) => { blank[f.key] = ""; });
    setRows((r) => [...r, blank]);
  };
  const rmRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const move = (i, dir) => setRows((r) => {
    const n = [...r]; const j = i + dir;
    if (j < 0 || j >= n.length) return r;
    [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const setField = (i, key, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));

  return (
    <div className="mt-1">
      <div className="flex items-center gap-2">
        <p className="text-sm text-muted-foreground">
          {(block.value || []).length} item{(block.value || []).length === 1 ? "" : "s"} · {shape.map((f) => f.label).join(" / ")}
        </p>
        <button type="button" onClick={() => setOpen((v) => !v)} className="cms-btn cms-btn-primary" data-testid={`cms-block-list-edit-${block.id}`}>
          {open ? "Close editor" : "Manage list"}
        </button>
        {block.is_override && !open && (
          <button type="button" onClick={doReset} className="cms-btn" data-testid={`cms-block-reset-${block.id}`}>
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        )}
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="rounded-xl border border-shell-border bg-white p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-6 h-6 grid place-items-center rounded-full bg-primary/10 text-primary text-[11px] font-bold">{i + 1}</span>
                <div className="flex flex-col ml-auto">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-0.5 disabled:opacity-30">▲</button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="p-0.5 disabled:opacity-30">▼</button>
                </div>
                <button type="button" onClick={() => rmRow(i)} className="rounded-full p-1.5 hover:bg-rose-500/10 text-rose-600 transition">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {shape.map((f) => (
                <div key={f.key} className="mb-2">
                  <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-muted-foreground">{f.label}</label>
                  {f.type === "richtext" ? (
                    <textarea rows={2} value={row[f.key] || ""} onChange={(e) => setField(i, f.key, e.target.value)}
                      className="w-full mt-1 rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
                  ) : (
                    <input type={f.type === "url" ? "url" : "text"} value={row[f.key] || ""} onChange={(e) => setField(i, f.key, e.target.value)}
                      className="w-full mt-1 rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
                  )}
                </div>
              ))}
            </div>
          ))}
          <button type="button" onClick={addRow} className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-dashed border-shell-border py-2.5 text-sm font-semibold text-muted-foreground hover:border-primary/40 hover:text-primary transition">
            <Plus className="w-4 h-4" /> Add row
          </button>
          <div className="flex items-center gap-2 mt-3">
            <button type="button" onClick={save} disabled={saving} className="cms-btn cms-btn-primary">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save list
            </button>
            {block.is_override && (
              <button type="button" onClick={doReset} className="cms-btn">
                <RotateCcw className="w-3 h-3" /> Reset to default
              </button>
            )}
            <button type="button" onClick={() => setOpen(false)} className="cms-btn cms-btn-ghost">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Media library panel (standalone view — was the modal earlier) ──── */
function MediaLibraryPanel() {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-1">Media Library</p>
      <p className="text-sm text-muted-foreground max-w-2xl mb-4">
        All uploaded assets and registered external URLs. Delete anything unused — but be careful, existing content blocks referencing the URL will show a broken image until reassigned.
      </p>
      <InlineMediaLibrary />
    </div>
  );
}


/* ── CMS edit activity — recent feed, top-edited blocks, editors ────── */
function ActivityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: body } = await api.get("/admin/cms/activity");
      setData(body);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (!data)   return <p className="text-sm text-muted-foreground italic">Nothing to show yet.</p>;

  return (
    <div className="grid md:grid-cols-[1fr_320px] gap-6" data-testid="cms-activity-panel">
      {/* Feed */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            Recent edits · {data.total_versions} total
          </p>
          <button type="button" onClick={load} className="cms-btn cms-btn-ghost" data-testid="cms-activity-refresh">
            <History className="w-3 h-3" /> Refresh
          </button>
        </div>
        {data.feed.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="cms-activity-empty">
            No edits yet — CMS changes will appear here.
          </p>
        ) : (
          <ol className="space-y-2">
            {data.feed.map((v, i) => (
              <li key={v.id} className="rounded-xl border border-shell-border bg-white p-3 flex items-start gap-3" data-testid={`cms-activity-entry-${i}`}>
                <span className={[
                  "shrink-0 mt-0.5 inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em]",
                  v.action === "reset"   ? "bg-slate-500/10 text-slate-700" :
                  v.action === "restore" ? "bg-sky-500/10 text-sky-800" :
                                            "bg-primary/10 text-primary",
                ].join(" ")}>{v.action}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{v.label}</p>
                  <code className="text-[10px] font-mono text-muted-foreground">{v.block_id}</code>
                  <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5 mt-0.5">
                    <User className="w-2.5 h-2.5" /> {v.created_by_email || "unknown"}
                    <span>·</span>
                    <Clock className="w-2.5 h-2.5" /> <RelativeTime iso={v.created_at} />
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Sidebar aggregates */}
      <div className="space-y-4">
        <SideCard title="Top-edited blocks" testid="cms-activity-top-blocks">
          {data.top_blocks.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Nothing yet.</p>
          ) : data.top_blocks.map((row, i) => (
            <div key={row.block_id} className="flex items-center gap-2 text-xs">
              <span className="w-5 shrink-0 text-muted-foreground">{i + 1}.</span>
              <span className="font-semibold truncate flex-1" title={row.block_id}>{row.label}</span>
              <span className="text-primary font-bold shrink-0">{row.count}</span>
            </div>
          ))}
        </SideCard>
        <SideCard title="Editor leaderboard" testid="cms-activity-editors">
          {data.editors.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Nothing yet.</p>
          ) : data.editors.map((row, i) => (
            <div key={row.email} className="flex items-center gap-2 text-xs">
              <span className="w-5 shrink-0 text-muted-foreground">{i + 1}.</span>
              <span className="font-semibold truncate flex-1" title={row.email}>{row.email}</span>
              <span className="text-primary font-bold shrink-0">{row.count}</span>
            </div>
          ))}
        </SideCard>
        <SideCard title="Last 14 days" testid="cms-activity-daily">
          {data.daily.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No edits in the last 14 days.</p>
          ) : (
            <div className="flex items-end gap-1 h-16">
              {data.daily.map((d) => {
                const max = Math.max(...data.daily.map((x) => x.count), 1);
                const h = Math.max(4, Math.round((d.count / max) * 60));
                return (
                  <div key={d.date} title={`${d.date} — ${d.count} edits`}
                       className="flex-1 bg-primary/70 rounded-t"
                       style={{ height: `${h}px` }} />
                );
              })}
            </div>
          )}
        </SideCard>
      </div>
    </div>
  );
}

function SideCard({ title, testid, children }) {
  return (
    <div className="rounded-2xl border border-shell-border bg-white p-4" data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">{title}</p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}


/* ── Inline (non-modal) media library — used in the Media tab ───────── */
function InlineMediaLibrary() {
  const [kind, setKind] = useState("image");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addExtOpen, setAddExtOpen] = useState(false);
  const [extUrl, setExtUrl] = useState("");
  const [extAlt, setExtAlt] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cms/media?kind=${kind}${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setItems(data?.media || []);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("kind", kind);
      await api.post("/admin/cms/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); }
  };

  const addExt = async () => {
    if (!extUrl.trim()) return;
    try {
      await api.post("/admin/cms/media", { url: extUrl.trim(), kind, alt: extAlt.trim() });
      setExtUrl(""); setExtAlt(""); setAddExtOpen(false); load();
      toast.success("Added");
    } catch (err) { toast.error(formatError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove from library? Blocks using this URL will show a broken image until updated.")) return;
    try { await api.delete(`/admin/cms/media/${id}`); load(); toast.success("Removed"); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div data-testid="cms-media-library">
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="inline-flex rounded-full bg-shell-border/30 p-1">
          {["image", "video"].map((k) => (
            <button key={k} onClick={() => setKind(k)} className={`rounded-full px-3 py-1 text-xs font-semibold transition ${kind === k ? "bg-primary text-white" : "text-muted-foreground"}`} data-testid={`cms-media-lib-kind-${k}`}>
              {k}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 placeholder="Search…"
                 className="w-full rounded-full border border-shell-border bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
        </div>
        <label className="cms-btn cms-btn-primary cursor-pointer">
          <input type="file" hidden accept={kind === "image" ? "image/*" : "video/*"} onChange={(e) => doUpload(e.target.files?.[0])} data-testid="cms-media-lib-upload" />
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload
        </label>
        <button type="button" onClick={() => setAddExtOpen((v) => !v)} className="cms-btn" data-testid="cms-media-lib-external-toggle">
          <ExternalLink className="w-3 h-3" /> Add URL
        </button>
      </div>

      {addExtOpen && (
        <div className="rounded-2xl border border-shell-border bg-shell-border/10 p-3 space-y-2 mb-4">
          <input type="url" value={extUrl} onChange={(e) => setExtUrl(e.target.value)} placeholder="Public URL"
            className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
          <input type="text" value={extAlt} onChange={(e) => setExtAlt(e.target.value)} placeholder="Alt (optional)"
            className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
          <button type="button" onClick={addExt} className="cms-btn cms-btn-primary" data-testid="cms-media-lib-external-save">Add to library</button>
        </div>
      )}

      {loading ? (
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">Library is empty for {kind}. Upload one above or paste an external URL.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {items.map((it) => {
            const full = it.url.startsWith("/api/") ? `${BACKEND_URL}${it.url}` : it.url;
            return (
              <div key={it.id} className="rounded-xl border border-shell-border bg-shell-border/10 overflow-hidden">
                {kind === "image" ? (
                  <img src={full} alt={it.alt || it.filename} className="w-full h-32 object-cover" />
                ) : (
                  <div className="w-full h-32 grid place-items-center bg-slate-900 text-white"><Video className="w-8 h-8 opacity-70" /></div>
                )}
                <div className="p-2 flex items-center gap-1">
                  <p className="text-[11px] truncate flex-1" title={it.filename}>{it.filename}</p>
                  <button type="button" onClick={() => remove(it.id)} className="text-rose-600 hover:bg-rose-500/10 rounded p-1" title="Remove">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ── Media library browser modal (standalone) ────────────────────────── */
function MediaLibraryBrowser({ onClose }) {
  const [kind, setKind] = useState("image");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addExtOpen, setAddExtOpen] = useState(false);
  const [extUrl, setExtUrl] = useState("");
  const [extAlt, setExtAlt] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cms/media?kind=${kind}${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setItems(data?.media || []);
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("kind", kind);
      await api.post("/admin/cms/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); }
  };

  const addExt = async () => {
    if (!extUrl.trim()) return;
    try {
      await api.post("/admin/cms/media", { url: extUrl.trim(), kind, alt: extAlt.trim() });
      setExtUrl(""); setExtAlt(""); setAddExtOpen(false); load();
      toast.success("Added");
    } catch (err) { toast.error(formatError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove from library? Blocks using this URL will show a broken image until updated.")) return;
    try { await api.delete(`/admin/cms/media/${id}`); load(); toast.success("Removed"); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="cms-drawer-backdrop" onClick={onClose}>
      <div className="cms-drawer cms-drawer-lg" onClick={(e) => e.stopPropagation()} data-testid="cms-media-library">
        <div className="flex items-center justify-between px-5 py-4 border-b border-shell-border">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary flex items-center gap-1.5">
              <ImageIcon className="w-3 h-3" /> Media Library
            </p>
            <h3 className="font-display text-xl mt-0.5">Uploads & external URLs</h3>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-shell-border/30">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-2 px-5 py-3 border-b border-shell-border flex-wrap">
          <div className="inline-flex rounded-full bg-shell-border/30 p-1">
            {["image", "video"].map((k) => (
              <button key={k} onClick={() => setKind(k)} className={`rounded-full px-3 py-1 text-xs font-semibold transition ${kind === k ? "bg-primary text-white" : "text-muted-foreground"}`} data-testid={`cms-media-lib-kind-${k}`}>
                {k}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && load()}
                   placeholder="Search…"
                   className="w-full rounded-full border border-shell-border bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
          </div>
          <label className="cms-btn cms-btn-primary cursor-pointer">
            <input type="file" hidden accept={kind === "image" ? "image/*" : "video/*"} onChange={(e) => doUpload(e.target.files?.[0])} data-testid="cms-media-lib-upload" />
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload
          </label>
          <button type="button" onClick={() => setAddExtOpen((v) => !v)} className="cms-btn" data-testid="cms-media-lib-external-toggle">
            <ExternalLink className="w-3 h-3" /> Add URL
          </button>
        </div>

        {addExtOpen && (
          <div className="px-5 py-3 border-b border-shell-border bg-shell-border/10 space-y-2">
            <input type="url" value={extUrl} onChange={(e) => setExtUrl(e.target.value)} placeholder="Public URL"
              className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
            <input type="text" value={extAlt} onChange={(e) => setExtAlt(e.target.value)} placeholder="Alt (optional)"
              className="w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary/40" />
            <button type="button" onClick={addExt} className="cms-btn cms-btn-primary" data-testid="cms-media-lib-external-save">Add to library</button>
          </div>
        )}

        <div className="p-5 flex-1 overflow-auto">
          {loading ? (
            <div className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Library is empty for {kind}. Upload one above or paste an external URL.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {items.map((it) => {
                const full = it.url.startsWith("/api/") ? `${BACKEND_URL}${it.url}` : it.url;
                return (
                  <div key={it.id} className="rounded-xl border border-shell-border bg-shell-border/10 overflow-hidden">
                    {kind === "image" ? (
                      <img src={full} alt={it.alt || it.filename} className="w-full h-32 object-cover" />
                    ) : (
                      <div className="w-full h-32 grid place-items-center bg-slate-900 text-white"><Video className="w-8 h-8 opacity-70" /></div>
                    )}
                    <div className="p-2 flex items-center gap-1">
                      <p className="text-[11px] truncate flex-1" title={it.filename}>{it.filename}</p>
                      <button type="button" onClick={() => remove(it.id)} className="text-rose-600 hover:bg-rose-500/10 rounded p-1" title="Remove">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
